import React from "react";
import { Alert, Button, Paper, Stack, Typography } from "@mui/material";
import {
  formatCareEvents,
  formatStaffTime,
  unsignedAge,
  signingReminder,
} from "./staffNotePresentation";
export default function StaffNoteCard({
  note,
  context,
  busy,
  onEdit,
  onWithdraw,
  onSign,
  onConfirmTranscript,
  onRetry,
  onAddendum,
  now = new Date(),
}) {
  if (!note.version)
    return (
      <Alert severity="warning">
        Original unavailable:{" "}
        {note.loadError || "Please retry loading your notes."}
      </Alert>
    );
  const signed = note.signature;
  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, borderRadius: 3 }}
      data-capture-id={note.captureId}
    >
      <Stack spacing={1}>
        {note.parentCaptureId ? (
          <Typography fontWeight={700}>
            Dated correction to a signed original
          </Typography>
        ) : null}
        <Typography>
          Care: {formatCareEvents(note.version.careEvents)}
        </Typography>
        <Typography color="text.secondary">
          Documented:{" "}
          {formatStaffTime(note.version.documentedAt, context.timeZone)} ·{" "}
          {context.timeZone}
        </Typography>
        <Typography fontWeight={700}>
          {signed
            ? `Signed · ${signed.signerName} · ${signed.providerTitle} · ${formatStaffTime(signed.signedAt, context.timeZone)}`
            : `Unsigned · ${unsignedAge(note, now, context.timeZone)}`}
        </Typography>
        {!signed && context.reminderDays ? (
          <Typography color="text.secondary">
            {signingReminder(note, now, context.timeZone, context.reminderDays)}
          </Typography>
        ) : null}
        <Typography sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
          {note.version.text}
        </Typography>
        {(note.audioAttachments || [])
          .filter((audio) => audio.url)
          .map((audio, index) => (
            <audio
              key={audio.path || index}
              controls
              src={audio.url}
              style={{ width: "100%" }}
            />
          ))}
        {!signed && !note.readiness?.ready ? (
          <Alert severity="info">{note.readiness?.reason}</Alert>
        ) : null}
        {note.processing?.status === "failed" ? (
          <Alert severity="warning">
            Organization failed: {note.processing.error} Your original is saved.
          </Alert>
        ) : null}
        {["pending", "working"].includes(note.processing?.status) ? (
          <Typography color="text.secondary">
            Organizing the saved original…
          </Typography>
        ) : null}
        {note.duplicateYesterday ? (
          <Alert severity="info">
            Same wording as yesterday — is this right?
          </Alert>
        ) : null}
        {note.addendumIds?.length ? (
          <Button component="a" href={`/sources/${note.captureId}`}>
            View original and correction records
          </Button>
        ) : null}
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {signed ? (
            <Button disabled={busy} onClick={onAddendum}>
              Add a correction
            </Button>
          ) : (
            <>
              <Button disabled={busy} onClick={onEdit}>
                {note.version.inputMode === "voice"
                  ? "Edit transcript / care date"
                  : "Edit original / care date"}
              </Button>
              <Button disabled={busy} onClick={onWithdraw}>
                Delete
              </Button>
              {note.version.inputMode === "voice" &&
              note.confirmedTranscriptVersion !== note.version.versionId ? (
                <Button
                  disabled={
                    busy ||
                    !note.version.audioPaths.length ||
                    !note.version.text.trim()
                  }
                  onClick={onConfirmTranscript}
                >
                  Confirm this transcript
                </Button>
              ) : null}
              <Button
                disabled={busy || !note.readiness?.ready}
                onClick={onSign}
              >
                Sign original
              </Button>
            </>
          )}
          {note.processing?.status === "failed" ? (
            <Button disabled={busy} onClick={onRetry}>
              Try organizing again
            </Button>
          ) : null}
        </Stack>
      </Stack>
    </Paper>
  );
}
